import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

/**
 * Format currency in Brazilian Real format
 */
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })?.format(value || 0);
};

/**
 * Format date in Brazilian format
 */
const formatDate = (date) => {
  return new Date(date)?.toLocaleDateString('pt-BR');
};

/**
 * Export dashboard data to PDF with enhanced KPI snapshots and forecasts
 */
export const exportToPDF = async (dashboardData, options = {}) => {
  const {
    includeCharts = true,
    includeMetrics = true,
    includeTransactions = true,
    chartElements = []
  } = options;

  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const pageHeight = doc?.internal?.pageSize?.getHeight();
    let yPosition = 20;

    // Header with timestamp
    doc?.setFontSize(20);
    doc?.setTextColor(31, 41, 55);
    doc?.text('JR Wise Tour - Relatório Financeiro', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 10;
    doc?.setFontSize(10);
    doc?.setTextColor(107, 114, 128);
    doc?.text(`Gerado em: ${formatDate(new Date())} às ${new Date()?.toLocaleTimeString('pt-BR')}`, pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 5;
    doc?.text(`Período: ${dashboardData?.reportPeriod?.start ? formatDate(dashboardData?.reportPeriod?.start) : 'N/A'} - ${dashboardData?.reportPeriod?.end ? formatDate(dashboardData?.reportPeriod?.end) : 'N/A'}`, pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 15;

    // Financial Metrics Section - KPI Snapshots
    if (includeMetrics && dashboardData) {
      doc?.setFontSize(14);
      doc?.setTextColor(31, 41, 55);
      doc?.text('📊 KPI Financeiros - Snapshot Atual', 14, yPosition);
      yPosition += 10;

      const metricsData = [
        ['Métrica', 'Valor', 'Status'],
        ['Faturamento Total', formatCurrency(dashboardData?.totalRevenue), '💰 Receita Bruta'],
        ['Pago ao Fornecedor', formatCurrency(dashboardData?.totalPaidToSuppliers), '💸 Custos Operacionais'],
        ['Receita Líquida', formatCurrency(dashboardData?.netProfit), dashboardData?.netProfit > 0 ? '✅ Lucro' : '⚠️ Prejuízo'],
        ['Pagamentos a Realizar', formatCurrency(dashboardData?.pendingPayables), `${dashboardData?.overduePayables || 0} vencidos`],
        ['Valores a Receber', formatCurrency(dashboardData?.pendingReceivables), `${dashboardData?.overdueReceivables || 0} vencidos`],
        [],
        ['INDICADORES DE ALERTA', '', ''],
        ['Pagamentos Vencidos', `${dashboardData?.overduePayables || 0}`, dashboardData?.overduePayables > 0 ? '🚨 Atenção Imediata' : '✓ Nenhum'],
        ['Recebíveis Vencidos', `${dashboardData?.overdueReceivables || 0}`, dashboardData?.overdueReceivables > 0 ? '🚨 Ação Necessária' : '✓ Nenhum']
      ];

      doc?.autoTable({
        startY: yPosition,
        head: [metricsData?.[0]],
        body: metricsData?.slice(1),
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 10, fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 70 },
          1: { halign: 'right', cellWidth: 50 },
          2: { cellWidth: 60 }
        }
      });

      yPosition = doc?.lastAutoTable?.finalY + 15;
    }

    // Charts Section with enhanced descriptions
    if (includeCharts && chartElements?.length > 0) {
      if (yPosition > pageHeight - 80) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(31, 41, 55);
      doc?.text('📈 Gráficos e Visualizações de Desempenho', 14, yPosition);
      yPosition += 10;

      for (const chartElement of chartElements) {
        if (yPosition > pageHeight - 100) {
          doc?.addPage();
          yPosition = 20;
        }

        try {
          const canvas = await html2canvas(chartElement, {
            scale: 2,
            backgroundColor: '#ffffff'
          });
          const imgData = canvas?.toDataURL('image/png');
          const imgWidth = pageWidth - 28;
          const imgHeight = (canvas?.height * imgWidth) / canvas?.width;

          if (yPosition + imgHeight > pageHeight - 20) {
            doc?.addPage();
            yPosition = 20;
          }

          doc?.addImage(imgData, 'PNG', 14, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 15;
        } catch (error) {
          console.error('Error capturing chart:', error);
        }
      }
    }

    // Cash Flow Forecast Section
    if (dashboardData?.cashFlowData && dashboardData?.cashFlowData?.length > 0) {
      if (yPosition > pageHeight - 100) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(31, 41, 55);
      doc?.text('💹 Fluxo de Caixa - Histórico Recente', 14, yPosition);
      yPosition += 10;

      const cashFlowTableData = [
        ['Data', 'Entradas', 'Saídas', 'Saldo Atual'],
        ...dashboardData?.cashFlowData?.slice(0, 15)?.map(cf => [
          formatDate(cf?.date),
          formatCurrency(cf?.income || 0),
          formatCurrency(cf?.expenses || 0),
          formatCurrency(cf?.actualBalance || 0)
        ])
      ];

      doc?.autoTable({
        startY: yPosition,
        head: [cashFlowTableData?.[0]],
        body: cashFlowTableData?.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { halign: 'right', cellWidth: 40 },
          2: { halign: 'right', cellWidth: 40 },
          3: { halign: 'right', cellWidth: 45, fontStyle: 'bold' }
        }
      });

      yPosition = doc?.lastAutoTable?.finalY + 15;
    }

    // Upcoming Payables Section
    if (dashboardData?.upcomingPayables && dashboardData?.upcomingPayables?.length > 0) {
      if (yPosition > pageHeight - 100) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(31, 41, 55);
      doc?.text('📅 Pagamentos Programados (Próximos 30 dias)', 14, yPosition);
      yPosition += 10;

      const payablesTableData = [
        ['Vencimento', 'Fornecedor', 'Valor', 'Negociação'],
        ...dashboardData?.upcomingPayables?.slice(0, 10)?.map(p => [
          formatDate(p?.dueDate),
          p?.supplierName || 'N/A',
          formatCurrency(p?.amount),
          p?.negotiationTitle || 'N/A'
        ])
      ];

      doc?.autoTable({
        startY: yPosition,
        head: [payablesTableData?.[0]],
        body: payablesTableData?.slice(1),
        theme: 'grid',
        headStyles: { fillColor: [239, 68, 68], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 50 },
          2: { halign: 'right', cellWidth: 35 },
          3: { cellWidth: 55 }
        }
      });

      yPosition = doc?.lastAutoTable?.finalY + 15;
    }

    // Upcoming Receivables Section
    if (dashboardData?.upcomingReceivables && dashboardData?.upcomingReceivables?.length > 0) {
      if (yPosition > pageHeight - 100) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(31, 41, 55);
      doc?.text('📅 Recebimentos Esperados (Próximos 30 dias)', 14, yPosition);
      yPosition += 10;

      const receivablesTableData = [
        ['Vencimento', 'Cliente', 'Valor', 'Negociação'],
        ...dashboardData?.upcomingReceivables?.slice(0, 10)?.map(r => [
          formatDate(r?.dueDate),
          r?.clientName || 'N/A',
          formatCurrency(r?.amount),
          r?.negotiationTitle || 'N/A'
        ])
      ];

      doc?.autoTable({
        startY: yPosition,
        head: [receivablesTableData?.[0]],
        body: receivablesTableData?.slice(1),
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 50 },
          2: { halign: 'right', cellWidth: 35 },
          3: { cellWidth: 55 }
        }
      });

      yPosition = doc?.lastAutoTable?.finalY + 15;
    }

    // Transactions Section
    if (includeTransactions && dashboardData?.recentTransactions) {
      if (yPosition > pageHeight - 100) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(31, 41, 55);
      doc?.text('💳 Transações Recentes', 14, yPosition);
      yPosition += 10;

      const transactionsData = [
        ['Data', 'Descrição', 'Tipo', 'Valor'],
        ...dashboardData?.recentTransactions?.slice(0, 20)?.map(t => [
          formatDate(t?.date),
          t?.description || 'N/A',
          t?.type === 'receivable' ? 'Recebível' : 'Pagável',
          formatCurrency(t?.amount)
        ])
      ];

      doc?.autoTable({
        startY: yPosition,
        head: [transactionsData?.[0]],
        body: transactionsData?.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 70 },
          2: { cellWidth: 30 },
          3: { halign: 'right', cellWidth: 35 }
        }
      });
    }

    // Footer on all pages
    const totalPages = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc?.setPage(i);
      doc?.setFontSize(8);
      doc?.setTextColor(107, 114, 128);
      doc?.text(
        `Página ${i} de ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc?.text(
        'JR Wise Tour - Sistema de Controle Financeiro',
        14,
        pageHeight - 10
      );
      doc?.text(
        `Exportado em ${formatDate(new Date())}`,
        pageWidth - 14,
        pageHeight - 10,
        { align: 'right' }
      );
    }

    // Save PDF
    const fileName = `relatorio-financeiro-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    doc?.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error('Erro ao exportar PDF: ' + error.message);
  }
};

/**
 * Export dashboard data to Excel with enhanced KPI snapshots and forecasts
 */
export const exportToExcel = (dashboardData, options = {}) => {
  const {
    includeMetrics = true,
    includeTransactions = true,
    includeSummary = true
  } = options;

  try {
    const workbook = XLSX?.utils?.book_new();

    // Summary Sheet with KPI Snapshots
    if (includeSummary && dashboardData) {
      const summaryData = [
        ['JR WISE TOUR - RELATÓRIO FINANCEIRO COMPLETO'],
        ['Gerado em:', `${formatDate(new Date())} às ${new Date()?.toLocaleTimeString('pt-BR')}`],
        ['Período:', `${dashboardData?.reportPeriod?.start ? formatDate(dashboardData?.reportPeriod?.start) : 'N/A'} - ${dashboardData?.reportPeriod?.end ? formatDate(dashboardData?.reportPeriod?.end) : 'N/A'}`],
        [],
        ['RESUMO EXECUTIVO - KPI SNAPSHOT'],
        ['Métrica', 'Valor', 'Observação'],
        ['Faturamento Total', dashboardData?.totalRevenue, 'Receita bruta de vendas'],
        ['Custos com Fornecedores', dashboardData?.totalPaidToSuppliers, 'Total pago aos fornecedores'],
        ['Receita Líquida', dashboardData?.netProfit, dashboardData?.netProfit > 0 ? 'LUCRO' : 'PREJUÍZO'],
        ['Valores a Receber', dashboardData?.pendingReceivables, `${dashboardData?.overdueReceivables || 0} vencidos`],
        ['Pagamentos Pendentes', dashboardData?.pendingPayables, `${dashboardData?.overduePayables || 0} vencidos`],
        [],
        ['INDICADORES DE ALERTA'],
        ['Pagamentos Vencidos', dashboardData?.overduePayables || 0, dashboardData?.overduePayables > 0 ? '🚨 ATENÇÃO IMEDIATA' : '✓ OK'],
        ['Recebíveis Vencidos', dashboardData?.overdueReceivables || 0, dashboardData?.overdueReceivables > 0 ? '🚨 AÇÃO NECESSÁRIA' : '✓ OK']
      ];

      const summarySheet = XLSX?.utils?.aoa_to_sheet(summaryData);
      
      // Set column widths
      summarySheet['!cols'] = [
        { wch: 35 },
        { wch: 20 },
        { wch: 40 }
      ];

      XLSX?.utils?.book_append_sheet(workbook, summarySheet, 'Resumo Executivo');
    }

    // Metrics Sheet - Detailed KPIs
    if (includeMetrics && dashboardData) {
      const metricsData = [
        ['MÉTRICAS FINANCEIRAS DETALHADAS'],
        [],
        ['Métrica', 'Valor', 'Status', 'Observações'],
        ['Faturamento Total', dashboardData?.totalRevenue, 'Total de vendas', 'Soma de todas as negociações'],
        ['Pago ao Fornecedor', dashboardData?.totalPaidToSuppliers, 'Total de pagamentos', 'Custos operacionais'],
        ['Receita Líquida', dashboardData?.netProfit, dashboardData?.netProfit > 0 ? 'Lucro' : 'Prejuízo', 'Faturamento - Custos'],
        ['Pagamentos a Realizar', dashboardData?.pendingPayables, `${dashboardData?.overduePayables || 0} vencidos`, 'Contas a pagar'],
        ['Valores a Receber', dashboardData?.pendingReceivables, `${dashboardData?.overdueReceivables || 0} vencidos`, 'Contas a receber'],
        [],
        ['INDICADORES DE DESEMPENHO (KPIs)'],
        ['Indicador', 'Valor', 'Meta', 'Status'],
        ['Margem de Lucro', 
          dashboardData?.totalRevenue > 0 
            ? `${((dashboardData?.netProfit / dashboardData?.totalRevenue) * 100)?.toFixed(2)}%` 
            : '0%',
          '20%',
          'Monitorar'
        ],
        ['Taxa de Inadimplência - Pagamentos', 
          dashboardData?.pendingPayables > 0
            ? `${((dashboardData?.overduePayables / dashboardData?.pendingPayables) * 100)?.toFixed(2)}%`
            : '0%',
          '< 5%',
          dashboardData?.overduePayables > 0 ? 'ALERTA' : 'OK'
        ],
        ['Taxa de Inadimplência - Recebíveis',
          dashboardData?.pendingReceivables > 0
            ? `${((dashboardData?.overdueReceivables / dashboardData?.pendingReceivables) * 100)?.toFixed(2)}%`
            : '0%',
          '< 5%',
          dashboardData?.overdueReceivables > 0 ? 'ALERTA' : 'OK'
        ]
      ];

      const metricsSheet = XLSX?.utils?.aoa_to_sheet(metricsData);
      
      // Set column widths
      metricsSheet['!cols'] = [
        { wch: 35 },
        { wch: 20 },
        { wch: 20 },
        { wch: 30 }
      ];

      XLSX?.utils?.book_append_sheet(workbook, metricsSheet, 'Métricas Detalhadas');
    }

    // Cash Flow Forecast Sheet
    if (dashboardData?.cashFlowData && dashboardData?.cashFlowData?.length > 0) {
      const cashFlowSheetData = [
        ['FLUXO DE CAIXA - HISTÓRICO RECENTE'],
        [],
        ['Data', 'Entradas', 'Saídas', 'Saldo Atual', 'Variação'],
        ...dashboardData?.cashFlowData?.map((cf, index, arr) => {
          const previousBalance = index > 0 ? arr?.[index - 1]?.actualBalance : cf?.actualBalance - (cf?.income - cf?.expenses);
          const variation = cf?.actualBalance - previousBalance;
          return [
            formatDate(cf?.date),
            cf?.income || 0,
            cf?.expenses || 0,
            cf?.actualBalance || 0,
            variation
          ];
        })
      ];

      const cashFlowSheet = XLSX?.utils?.aoa_to_sheet(cashFlowSheetData);
      
      // Set column widths
      cashFlowSheet['!cols'] = [
        { wch: 15 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 }
      ];

      XLSX?.utils?.book_append_sheet(workbook, cashFlowSheet, 'Fluxo de Caixa');
    }

    // Upcoming Payables Sheet
    if (dashboardData?.upcomingPayables && dashboardData?.upcomingPayables?.length > 0) {
      const payablesSheetData = [
        ['PAGAMENTOS PROGRAMADOS (PRÓXIMOS 30 DIAS)'],
        [],
        ['Vencimento', 'Fornecedor', 'Valor', 'Negociação', 'Método Pagamento'],
        ...dashboardData?.upcomingPayables?.map(p => [
          formatDate(p?.dueDate),
          p?.supplierName || 'N/A',
          p?.amount,
          p?.negotiationTitle || 'N/A',
          p?.paymentMethod || 'N/A'
        ])
      ];

      const payablesSheet = XLSX?.utils?.aoa_to_sheet(payablesSheetData);
      
      // Set column widths
      payablesSheet['!cols'] = [
        { wch: 15 },
        { wch: 30 },
        { wch: 15 },
        { wch: 35 },
        { wch: 20 }
      ];

      XLSX?.utils?.book_append_sheet(workbook, payablesSheet, 'Pagamentos Programados');
    }

    // Upcoming Receivables Sheet
    if (dashboardData?.upcomingReceivables && dashboardData?.upcomingReceivables?.length > 0) {
      const receivablesSheetData = [
        ['RECEBIMENTOS ESPERADOS (PRÓXIMOS 30 DIAS)'],
        [],
        ['Vencimento', 'Cliente', 'Valor', 'Negociação', 'Método Recebimento'],
        ...dashboardData?.upcomingReceivables?.map(r => [
          formatDate(r?.dueDate),
          r?.clientName || 'N/A',
          r?.amount,
          r?.negotiationTitle || 'N/A',
          r?.paymentMethod || 'N/A'
        ])
      ];

      const receivablesSheet = XLSX?.utils?.aoa_to_sheet(receivablesSheetData);
      
      // Set column widths
      receivablesSheet['!cols'] = [
        { wch: 15 },
        { wch: 30 },
        { wch: 15 },
        { wch: 35 },
        { wch: 20 }
      ];

      XLSX?.utils?.book_append_sheet(workbook, receivablesSheet, 'Recebimentos Esperados');
    }

    // Transactions Sheet
    if (includeTransactions && dashboardData?.recentTransactions) {
      const transactionsData = [
        ['TRANSAÇÕES RECENTES'],
        [],
        ['Data', 'Descrição', 'Tipo', 'Valor', 'Conta Bancária'],
        ...dashboardData?.recentTransactions?.map(t => [
          formatDate(t?.date),
          t?.description || 'N/A',
          t?.type === 'receivable' ? 'Recebível' : 'Pagável',
          t?.amount,
          t?.bankAccount || 'N/A'
        ])
      ];

      const transactionsSheet = XLSX?.utils?.aoa_to_sheet(transactionsData);
      
      // Set column widths
      transactionsSheet['!cols'] = [
        { wch: 15 },
        { wch: 40 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 }
      ];

      XLSX?.utils?.book_append_sheet(workbook, transactionsSheet, 'Transações');
    }

    // Detailed Analysis Sheet
    if (dashboardData) {
      const analysisData = [
        ['ANÁLISE FINANCEIRA DETALHADA'],
        [],
        ['Indicador', 'Valor Atual', 'Benchmark', 'Observação'],
        ['Taxa de Lucro', 
          dashboardData?.totalRevenue > 0 
            ? `${((dashboardData?.netProfit / dashboardData?.totalRevenue) * 100)?.toFixed(2)}%` 
            : '0%',
          '15-25%',
          'Relação entre lucro líquido e faturamento bruto'
        ],
        ['Inadimplência Pagamentos', 
          dashboardData?.pendingPayables > 0
            ? `${((dashboardData?.overduePayables / dashboardData?.pendingPayables) * 100)?.toFixed(2)}%`
            : '0%',
          '< 5%',
          'Percentual de pagamentos atrasados sobre total pendente'
        ],
        ['Inadimplência Recebíveis',
          dashboardData?.pendingReceivables > 0
            ? `${((dashboardData?.overdueReceivables / dashboardData?.pendingReceivables) * 100)?.toFixed(2)}%`
            : '0%',
          '< 5%',
          'Percentual de recebíveis vencidos sobre total a receber'
        ],
        [],
        ['FLUXO DE CAIXA PROJETADO'],
        ['Componente', 'Valor', 'Detalhes'],
        ['Total a Receber', dashboardData?.pendingReceivables, 'Valores pendentes de recebimento nos próximos 30 dias'],
        ['Total a Pagar', dashboardData?.pendingPayables, 'Valores pendentes de pagamento nos próximos 30 dias'],
        ['Saldo Projetado', dashboardData?.pendingReceivables - dashboardData?.pendingPayables, 'Diferença entre recebíveis e pagáveis (próximos 30 dias)'],
        [],
        ['RECOMENDAÇÕES'],
        ['Área', 'Status', 'Ação Sugerida'],
        ['Inadimplência', 
          (dashboardData?.overduePayables + dashboardData?.overdueReceivables) > 0 ? 'CRÍTICO' : 'OK',
          (dashboardData?.overduePayables + dashboardData?.overdueReceivables) > 0 
            ? 'Revisar títulos vencidos e tomar ações de cobrança/pagamento' 
            : 'Continuar monitoramento preventivo'
        ],
        ['Fluxo de Caixa',
          (dashboardData?.pendingReceivables - dashboardData?.pendingPayables) < 0 ? 'ATENÇÃO' : 'OK',
          (dashboardData?.pendingReceivables - dashboardData?.pendingPayables) < 0
            ? 'Planejar captação ou renegociação de pagamentos' :'Manter acompanhamento dos vencimentos'
        ],
        ['Margem de Lucro',
          dashboardData?.totalRevenue > 0 && ((dashboardData?.netProfit / dashboardData?.totalRevenue) * 100) < 15 ? 'ATENÇÃO' : 'OK',
          dashboardData?.totalRevenue > 0 && ((dashboardData?.netProfit / dashboardData?.totalRevenue) * 100) < 15
            ? 'Revisar precificação e custos operacionais'
            : 'Margem dentro do esperado'
        ]
      ];

      const analysisSheet = XLSX?.utils?.aoa_to_sheet(analysisData);
      
      // Set column widths
      analysisSheet['!cols'] = [
        { wch: 30 },
        { wch: 20 },
        { wch: 20 },
        { wch: 60 }
      ];

      XLSX?.utils?.book_append_sheet(workbook, analysisSheet, 'Análise e Recomendações');
    }

    // Generate and save Excel file
    const excelBuffer = XLSX?.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `relatorio-financeiro-completo-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`;
    saveAs(data, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Erro ao exportar Excel: ' + error.message);
  }
};

/**
 * Export specific chart to image
 */
export const exportChartToImage = async (chartElement, fileName = 'grafico') => {
  try {
    const canvas = await html2canvas(chartElement, {
      scale: 3,
      backgroundColor: '#ffffff'
    });
    
    canvas?.toBlob((blob) => {
      saveAs(blob, `${fileName}-${new Date()?.toISOString()?.split('T')?.[0]}.png`);
    });

    return { success: true };
  } catch (error) {
    console.error('Error exporting chart:', error);
    throw new Error('Erro ao exportar gráfico: ' + error.message);
  }
};

/**
 * Export utility functions for generating CSV and Excel files
 */

/**
 * Convert array of objects to CSV format
 */
export const convertToCSV = (data, headers) => {
  if (!data || data?.length === 0) return '';

  // Create header row
  const headerRow = headers?.map(h => h?.label)?.join(',');
  
  // Create data rows
  const dataRows = data?.map(row => {
    return headers?.map(header => {
      let value = row?.[header?.key];
      
      // Handle special cases
      if (value === null || value === undefined) {
        value = '';
      } else if (typeof value === 'object') {
        value = JSON.stringify(value);
      } else if (typeof value === 'string' && (value?.includes(',') || value?.includes('"'))) {
        value = `"${value?.replace(/"/g, '""')}"`;
      }
      
      return value;
    })?.join(',');
  })?.join('\n');

  return `${headerRow}\n${dataRows}`;
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link?.setAttribute('href', url);
  link?.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body?.appendChild(link);
  link?.click();
  document.body?.removeChild(link);
};

/**
 * Format date for export
 */
export const formatDateForExport = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date?.toLocaleDateString('pt-BR');
};

/**
 * Format currency for export
 */
export const formatCurrencyForExport = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return `R$ ${parseFloat(value)?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Export expenses to CSV
 */
export const exportExpensesToCSV = (expenses, filename = 'despesas.csv') => {
  const headers = [
    { key: 'description', label: 'Descrição' },
    { key: 'categoryName', label: 'Categoria' },
    { key: 'amount', label: 'Valor' },
    { key: 'expenseDate', label: 'Data' },
    { key: 'status', label: 'Status' },
    { key: 'isRecurring', label: 'Recorrente' },
    { key: 'recurrenceFrequency', label: 'Frequência' },
    { key: 'installmentInfo', label: 'Parcelas' },
    { key: 'notes', label: 'Observações' }
  ];

  const formattedData = expenses?.map(expense => ({
    description: expense?.description || '',
    categoryName: expense?.category?.name || '',
    amount: formatCurrencyForExport(expense?.amount),
    expenseDate: formatDateForExport(expense?.expenseDate),
    status: getStatusLabel(expense?.status),
    isRecurring: expense?.isRecurring ? 'Sim' : 'Não',
    recurrenceFrequency: getFrequencyLabel(expense?.recurrenceFrequency),
    installmentInfo: expense?.installmentCount 
      ? `${expense?.currentInstallment}/${expense?.installmentCount}`
      : '',
    notes: expense?.notes || ''
  }));

  const csv = convertToCSV(formattedData, headers);
  downloadCSV(csv, filename);
};

/**
 * Export negotiations to CSV
 */
export const exportNegotiationsToCSV = (negotiations, filename = 'negociacoes.csv') => {
  const headers = [
    { key: 'clientName', label: 'Cliente' },
    { key: 'supplierName', label: 'Fornecedor' },
    { key: 'negotiationDate', label: 'Data' },
    { key: 'totalValue', label: 'Valor Total' },
    { key: 'supplierCost', label: 'Custo Fornecedor' },
    { key: 'profitMargin', label: 'Lucro' },
    { key: 'status', label: 'Status' },
    { key: 'receivablesStatus', label: 'Status Recebimentos' },
    { key: 'notes', label: 'Observações' }
  ];

  const formattedData = negotiations?.map(neg => ({
    clientName: neg?.client?.name || '',
    supplierName: neg?.supplier?.name || '',
    negotiationDate: formatDateForExport(neg?.negotiationDate),
    totalValue: formatCurrencyForExport(neg?.totalValue),
    supplierCost: formatCurrencyForExport(neg?.supplierCost),
    profitMargin: formatCurrencyForExport(neg?.profitMargin),
    status: getNegotiationStatusLabel(neg?.status),
    receivablesStatus: getReceivablesStatusSummary(neg?.receivables),
    notes: neg?.notes || ''
  }));

  const csv = convertToCSV(formattedData, headers);
  downloadCSV(csv, filename);
};

/**
 * Export negotiations with detailed receivables
 */
export const exportNegotiationsDetailedToCSV = (negotiations, filename = 'negociacoes_detalhadas.csv') => {
  const headers = [
    { key: 'clientName', label: 'Cliente' },
    { key: 'supplierName', label: 'Fornecedor' },
    { key: 'negotiationDate', label: 'Data Negociação' },
    { key: 'receivableDueDate', label: 'Vencimento' },
    { key: 'receivableAmount', label: 'Valor Recebível' },
    { key: 'receivableStatus', label: 'Status Recebível' },
    { key: 'operator', label: 'Operador' },
    { key: 'totalValue', label: 'Valor Total Negociação' },
    { key: 'profitMargin', label: 'Lucro' }
  ];

  const formattedData = [];
  
  negotiations?.forEach(neg => {
    if (neg?.receivables && neg?.receivables?.length > 0) {
      neg?.receivables?.forEach(rec => {
        formattedData?.push({
          clientName: neg?.client?.name || '',
          supplierName: neg?.supplier?.name || '',
          negotiationDate: formatDateForExport(neg?.negotiationDate),
          receivableDueDate: formatDateForExport(rec?.due_date),
          receivableAmount: formatCurrencyForExport(rec?.amount),
          receivableStatus: getReceivableStatusLabel(rec?.status),
          operator: rec?.operator || '',
          totalValue: formatCurrencyForExport(neg?.totalValue),
          profitMargin: formatCurrencyForExport(neg?.profitMargin)
        });
      });
    } else {
      formattedData?.push({
        clientName: neg?.client?.name || '',
        supplierName: neg?.supplier?.name || '',
        negotiationDate: formatDateForExport(neg?.negotiationDate),
        receivableDueDate: '',
        receivableAmount: '',
        receivableStatus: 'Sem Recebíveis',
        operator: '',
        totalValue: formatCurrencyForExport(neg?.totalValue),
        profitMargin: formatCurrencyForExport(neg?.profitMargin)
      });
    }
  });

  const csv = convertToCSV(formattedData, headers);
  downloadCSV(csv, filename);
};

/**
 * Helper function to get status label in Portuguese
 */
const getStatusLabel = (status) => {
  const statusMap = {
    'pending': 'Pendente',
    'approved': 'Aprovado',
    'rejected': 'Rejeitado',
    'paid': 'Pago'
  };
  return statusMap?.[status] || status;
};

/**
 * Helper function to get frequency label in Portuguese
 */
const getFrequencyLabel = (frequency) => {
  const frequencyMap = {
    'once': 'Única',
    'weekly': 'Semanal',
    'biweekly': 'Quinzenal',
    'monthly': 'Mensal',
    'quarterly': 'Trimestral',
    'yearly': 'Anual'
  };
  return frequencyMap?.[frequency] || '';
};

/**
 * Helper function to get negotiation status label
 */
const getNegotiationStatusLabel = (status) => {
  const statusMap = {
    'draft': 'Rascunho',
    'active': 'Ativo',
    'completed': 'Concluído',
    'cancelled': 'Cancelado'
  };
  return statusMap?.[status] || status;
};

/**
 * Helper function to get receivable status label
 */
const getReceivableStatusLabel = (status) => {
  const statusMap = {
    'pending': 'Pendente',
    'received': 'Recebido',
    'overdue': 'Vencido',
    'cancelled': 'Cancelado'
  };
  return statusMap?.[status] || status;
};

/**
 * Helper function to get receivables status summary
 */
const getReceivablesStatusSummary = (receivables) => {
  if (!receivables || receivables?.length === 0) return 'Sem Recebíveis';
  
  const pending = receivables?.filter(r => r?.status === 'pending')?.length;
  const received = receivables?.filter(r => r?.status === 'received')?.length;
  const overdue = receivables?.filter(r => r?.status === 'overdue')?.length;
  
  const parts = [];
  if (overdue > 0) parts?.push(`${overdue} vencido(s)`);
  if (pending > 0) parts?.push(`${pending} pendente(s)`);
  if (received > 0) parts?.push(`${received} recebido(s)`);
  
  return parts?.join(', ') || 'Sem Status';
};

/**
 * Export expenses by month to CSV
 */
export const exportExpensesByMonthToCSV = (expenses, month, year, filename) => {
  const filteredExpenses = expenses?.filter(expense => {
    const expenseDate = new Date(expense.expenseDate);
    return expenseDate?.getMonth() === month && expenseDate?.getFullYear() === year;
  });

  const defaultFilename = `despesas_${year}_${String(month + 1)?.padStart(2, '0')}.csv`;
  exportExpensesToCSV(filteredExpenses, filename || defaultFilename);
};

/**
 * Export negotiations by month to CSV
 */
export const exportNegotiationsByMonthToCSV = (negotiations, month, year, filename) => {
  const filteredNegotiations = negotiations?.filter(neg => {
    const negDate = new Date(neg.negotiationDate);
    return negDate?.getMonth() === month && negDate?.getFullYear() === year;
  });

  const defaultFilename = `negociacoes_${year}_${String(month + 1)?.padStart(2, '0')}.csv`;
  exportNegotiationsDetailedToCSV(filteredNegotiations, filename || defaultFilename);
};
function exportHelpers(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: exportHelpers is not implemented yet.', args);
  return null;
}

export default exportHelpers;